import { useForm, usePage } from "@inertiajs/react"
import { MessageSquare, Send } from "lucide-react"
import {
  type FormEvent,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import { helpRequestsPath } from "@/routes"
import type { Auth } from "@/types"

type HelpEntrypoint = "desktop_widget" | "mobile_menu"

interface HelpContextValue {
  openHelp: (entrypoint?: HelpEntrypoint) => void
}

interface HelpFormData {
  contact_email: string
  message: string
}

type HelpFormErrors = Partial<Record<keyof HelpFormData, string | string[]>>

const HelpContext = createContext<HelpContextValue | null>(null)

function currentContactEmail(auth?: Auth | null) {
  return auth?.user?.email ?? ""
}

function errorMessage(error?: string | string[]) {
  return Array.isArray(error) ? error[0] : error
}

export function HelpProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [entrypoint, setEntrypoint] = useState<HelpEntrypoint>("desktop_widget")

  const openHelp = (nextEntrypoint: HelpEntrypoint = "desktop_widget") => {
    setEntrypoint(nextEntrypoint)
    setOpen(true)
  }

  return (
    <HelpContext.Provider value={{ openHelp }}>
      {children}
      <HelpWidget open={open} onOpenChange={setOpen} entrypoint={entrypoint} />
    </HelpContext.Provider>
  )
}

export function useHelp() {
  const context = useContext(HelpContext)

  if (!context) {
    throw new Error("useHelp must be used within HelpProvider")
  }

  return context
}

const HELP_UI_ENABLED = false

export function HelpMenuItem({ onBeforeOpen }: { onBeforeOpen?: () => void }) {
  const { openHelp } = useHelp()

  if (!HELP_UI_ENABLED) return null

  return (
    <DropdownMenuItem
      onSelect={() => {
        onBeforeOpen?.()
        window.setTimeout(() => openHelp("mobile_menu"), 0)
      }}
    >
      <MessageSquare className="mr-2" />
      How can we help?
    </DropdownMenuItem>
  )
}

function HelpWidget({
  open,
  onOpenChange,
  entrypoint,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entrypoint: HelpEntrypoint
}) {
  const page = usePage<{ auth?: Auth | null }>()
  const auth = page.props.auth
  const isMobile = useIsMobile()
  const email = currentContactEmail(auth)
  const { data, setData, post, processing, errors, clearErrors, transform } =
    useForm<HelpFormData>({
      contact_email: email,
      message: "",
    })

  useEffect(() => {
    if (!open) setData("contact_email", email)
  }, [email, open, setData])

  const resetForm = () => {
    clearErrors()
    setData("contact_email", email)
    setData("message", "")
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) resetForm()
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    transform((formData) => ({
      help_request: {
        ...formData,
        entrypoint,
        page_path: page.url,
      },
    }))

    post(helpRequestsPath(), {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        onOpenChange(false)
        resetForm()
      },
    })
  }

  const content = (
    <HelpForm
      data={data}
      errors={errors}
      processing={processing}
      setData={setData}
      onSubmit={handleSubmit}
    />
  )

  return (
    <>
      <DesktopHelpTrigger />
      {isMobile ? (
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="bottom"
            className="max-h-[85dvh] overflow-y-auto rounded-t-xl px-5 pb-8"
          >
            <div className="bg-muted mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full" />
            <SheetHeader>
              <SheetTitle>How can we help?</SheetTitle>
              <SheetDescription>
                Ask a question, report a problem, or tell us what you are trying
                to do.
              </SheetDescription>
            </SheetHeader>
            {content}
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogContent className="sm:max-w-lg">
            <DialogTitle>How can we help?</DialogTitle>
            <DialogDescription>
              Ask a question, report a problem, or tell us what you are trying
              to do.
            </DialogDescription>
            {content}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

function DesktopHelpTrigger() {
  const { openHelp } = useHelp()

  if (!HELP_UI_ENABLED) return null

  return (
    <div className="pointer-events-none fixed right-6 bottom-6 z-40 hidden md:block">
      <Button
        type="button"
        variant="outline"
        className="bg-background/95 pointer-events-auto rounded-full border-amber-200/70 px-4 py-5 shadow-lg backdrop-blur"
        onClick={() => openHelp("desktop_widget")}
      >
        <MessageSquare className="text-pulse" />
        How can we help?
      </Button>
    </div>
  )
}

function HelpForm({
  data,
  errors,
  processing,
  setData,
  onSubmit,
}: {
  data: HelpFormData
  errors: HelpFormErrors
  processing: boolean
  setData: {
    (key: "contact_email", value: string): void
    (key: "message", value: string): void
  }
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 pt-2">
      <div className="space-y-1.5">
        <Label htmlFor="help-message">Message</Label>
        <div className="relative">
          <textarea
            id="help-message"
            value={data.message}
            onChange={(event) => setData("message", event.target.value)}
            placeholder="Ask a question, report a problem, or tell us what you're trying to do."
            className={cn(
              "placeholder:text-muted-foreground dark:bg-input/30 border-input w-full min-w-0 rounded-md border bg-transparent px-3 py-2.5 text-base leading-relaxed shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
              "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
              "min-h-32 resize-none sm:min-h-40",
            )}
            maxLength={2000}
            disabled={processing}
          />
          <span className="text-muted-foreground pointer-events-none absolute right-3 bottom-2 text-xs tabular-nums">
            {data.message.length}/2000
          </span>
        </div>
        {errorMessage(errors.message) && (
          <p className="text-destructive text-sm">
            {errorMessage(errors.message)}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="help-contact-email">Best email for follow-up</Label>
        <Input
          id="help-contact-email"
          type="email"
          value={data.contact_email}
          onChange={(event) => setData("contact_email", event.target.value)}
          placeholder="name@company.com"
          disabled={processing}
        />
        {errorMessage(errors.contact_email) && (
          <p className="text-destructive text-sm">
            {errorMessage(errors.contact_email)}
          </p>
        )}
      </div>

      <div className="flex items-center justify-end pt-2">
        <Button
          type="submit"
          disabled={
            processing || !data.message.trim() || !data.contact_email.trim()
          }
        >
          <Send />
          {processing ? "Sending..." : "Send message"}
        </Button>
      </div>
    </form>
  )
}
